/**
 * Preisautomatik · Einstellungen je Apartment (`/dynamic-pricing/configuration?apartment=…`, Board
 * „Preisautomatik · Einstellungen“): Kopf mit Apartmentname und -wahl, darunter die Seitenzustände
 * ohne Apartment, lädt, offline, Fehler mit Grund – oder das Formular. Scheitert nur ein Nachladen,
 * bleibt das Formular samt Entwurf stehen.
 */
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { problemText, useApiProblem } from '@/shared/api';
import { SurfaceStaleHint } from '@/shared/components';
import { useObjectScope } from '@/shared/object-scope';
import { useDynamicPricingPropertyScope } from '../useDynamicPricingPropertyScope';
import { ConfigurationDiscardDialog } from './ConfigurationDiscardDialog';
import { ConfigurationHeader, ConfigurationHeaderChips } from './ConfigurationHeader';
import { ConfigurationPageState, pageState } from './ConfigurationPageState';
import { stamp } from './configurationFormat';
import {
  checkProblemText,
  dialogProblem,
  needsReload,
  saveProblemText,
} from './configurationProblemText';
import { PricingConfigurationForm } from './PricingConfigurationForm';
import { useConfigurationApartments } from './useConfigurationApartments';
import { usePricingConfiguration } from './usePricingConfiguration';

export function DynamicPricingConfigurationPage() {
  const navigate = useNavigate();
  const scope = useObjectScope();
  const apartmentId = useDynamicPricingPropertyScope();
  const apartments = useConfigurationApartments();
  const pricing = usePricingConfiguration(apartmentId);
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const { configuration, profiles, activeProfile } = pricing;
  const data = configuration.data;
  const firstLoadError = (!data && configuration.error) || (!profiles.data && profiles.error);
  const problem = useApiProblem(firstLoadError || null);
  const profileProblem = useApiProblem(activeProfile.error ?? null);
  const name = apartments.find((apartment) => apartment.id === apartmentId)?.name;
  const state = pageState({ apartmentId, scope, configuration, profiles, activeProfile });
  const go = (id: string) =>
    void navigate({ to: '/dynamic-pricing/configuration', search: id ? { apartment: id } : {} });
  const retry = () => {
    if (state === 'scopeError') scope.retry();
    if (!data) void configuration.refetch();
    if (!profiles.data) void profiles.refetch();
  };

  return (
    <div className="w-full min-w-0">
      <ConfigurationHeader
        title={name ?? 'Einstellungen'}
        chips={
          data && apartmentId ? (
            <ConfigurationHeaderChips
              version={`Aktuelle Version ${data.versionNumber} · gültig seit ${stamp(data.effectiveFrom)}`}
              dirty={dirty}
            />
          ) : (
            'Einstellungen der Preisautomatik je Apartment'
          )
        }
        apartments={apartments}
        apartmentId={apartmentId}
        onApartment={(id) => (dirty && id !== apartmentId ? setPending(id) : go(id))}
      />
      <div className="relative z-10 -mt-[84px] flex flex-col gap-4 md:px-5">
        {state !== 'ready' || !data ? (
          <ConfigurationPageState
            state={state}
            reason={problemText(problem, 'Die Einstellungen konnten nicht geladen werden.')}
            onRetry={retry}
            retrying={configuration.isFetching || profiles.isFetching || scope.isFetching}
          />
        ) : (
          <>
            {(configuration.error || profiles.error) && (
              <SurfaceStaleHint
                onRetry={() => void configuration.refetch()}
                retrying={configuration.isFetching}
              />
            )}
            <PricingConfigurationForm
              key={apartmentId}
              propertyId={apartmentId}
              apartmentName={name}
              configuration={data.configuration}
              versionNumber={data.versionNumber}
              effectiveFrom={data.effectiveFrom}
              warnings={data.warnings}
              profiles={profiles.data ?? []}
              activeProfile={activeProfile.data}
              activeProfileProblem={
                activeProfile.error
                  ? problemText(profileProblem, 'Die aktive Profilversion ist nicht ladbar.')
                  : null
              }
              onRetryActiveProfile={() => void activeProfile.refetch()}
              validation={pricing.validation}
              publicationControlsAvailable={data.publicationControlsAvailable}
              busy={pricing.isValidating || pricing.isPreviewing || pricing.isSaving}
              checking={pricing.isValidating}
              previewing={pricing.isPreviewing}
              saving={pricing.isSaving}
              saveErrorCode={pricing.saveError?.title}
              problem={checkProblemText(pricing.checkError) ?? saveProblemText(pricing.saveError)}
              dialogProblem={dialogProblem({
                save: pricing.saveError,
                check: pricing.checkError,
                preview: pricing.previewError,
              })}
              onReload={
                needsReload(pricing.saveError) || needsReload(pricing.previewError)
                  ? pricing.reload
                  : undefined
              }
              saved={pricing.saved}
              onDirtyChange={setDirty}
              onValidate={pricing.validate}
              onPreview={pricing.preview}
              onSave={pricing.save}
            />
          </>
        )}
      </div>
      <ConfigurationDiscardDialog
        open={pending !== null}
        switching
        onKeep={() => setPending(null)}
        onDiscard={() => {
          if (pending !== null) go(pending);
          setPending(null);
        }}
      />
    </div>
  );
}
